import { useRouteLoaderData } from 'react-router';
import { preloadQuery } from '@/apollo/apollo-client';
import { getApolloClient } from '@/apollo/apollo-client-instance';
import { StreamQuery } from '@/routes/_app.p.streams_.$id/stream-query.ts';
import { sessionContext } from '@/session';
import { Route } from './+types/route';
import { UnauthenticatedStreamQuery } from './unauthenticated-stream-query';

export const streamLoader = async (args: Route.ClientLoaderArgs) => {
  const authenticated = args.context.get(sessionContext).kind !== 'guest';
  const loader = authenticated
    ? authenticatedStreamLoader
    : unauthenticatedStreamLoader;
  return { ...(await loader(args)), authenticated };
};

export const authenticatedStreamLoader = ({
  params,
}: Route.ClientLoaderArgs) => {
  const id = params.id;

  return {
    streamQueryRef: preloadQuery(StreamQuery, {
      variables: {
        id,
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    }),
  };
};

const unauthenticatedStreamLoader = async ({
  params: { id },
  serverLoader,
}: Route.ClientLoaderArgs) => {
  const unauthenticatedData = (await serverLoader())?.unauthenticatedData;

  if (unauthenticatedData) {
    getApolloClient().writeQuery({
      query: UnauthenticatedStreamQuery,
      variables: { id },
      data: unauthenticatedData,
    });
  }

  return {
    unauthenticatedData,
    streamQueryRef: preloadQuery(UnauthenticatedStreamQuery, {
      variables: { id },
      errorPolicy: 'all',
    }),
  };
};

export type AuthenticatedStreamLoaderData = ReturnType<
  typeof authenticatedStreamLoader
> & {
  authenticated: boolean;
};

export type UnauthenticatedStreamLoaderData = Awaited<
  ReturnType<typeof unauthenticatedStreamLoader>
> & { authenticated: boolean };

export const useStreamLoaderData = () =>
  useRouteLoaderData(
    'routes/_app.p.streams_.$id',
  ) as AuthenticatedStreamLoaderData & UnauthenticatedStreamLoaderData;
